import { useState } from 'react';

import { Empty, Field } from '../../components/ui/bits.jsx';
import { useUI } from '../../components/ui/feedback.jsx';
import { fmtDate, today, dni, vaje, leti as letiWord } from '../../lib/format.js';
import * as api from '../../lib/api.js';
import { Icon } from '../../components/ui/Icon.jsx';
import { t } from '../../i18n/index.js';

/** Dnevnik prisotnosti: dnevi z vajami in leti se vpišejo sami, ostale doda tečajnik. */
export function Attendance({ uid, days, onChanged, readOnly = false }) {
  const { toast, confirm } = useUI();
  const [form, setForm] = useState({ attendance_date: '', note: '' });
  const [busy, setBusy] = useState(false);

  const add = async (e) => {
    e.preventDefault();
    if (!form.attendance_date) return;
    setBusy(true);
    try {
      await api.addAttendance(uid, form);
      setForm({ attendance_date: '', note: '' });
      toast.ok(t('Dan je dodan v dnevnik.'));
      await onChanged();
    } catch (err) {
      toast.bad(err.message ?? t('Dneva ni bilo mogoče dodati.'));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (day) => {
    const ok = await confirm({
      title: t('Izbrišem ta dan?'),
      body: t('{d} bo odstranjen iz dnevnika prisotnosti.', { d: fmtDate(day.date) }),
      confirmLabel: t('Izbriši'),
      danger: true,
    });
    if (!ok) return;
    try {
      await api.deleteAttendance(day.manualId);
      toast.ok(t('Dan je izbrisan.'));
      await onChanged();
    } catch (err) {
      toast.bad(err.message ?? t('Brisanje ni uspelo.'));
    }
  };

  return (
    <div className="stack stack--loose">
      <section className="card card--brand card--pad row">
        <Icon name="calendar" size={26} className="t-brand" />
        <div className="spacer">
          <h2 className="t-lg">{t('Dnevnik prisotnosti')}</h2>
          <p className="t-sm t-dim">{t('Dnevi z vajami in leti se vpišejo samodejno.')}</p>
        </div>
        <span className="t-2xl t-black t-brand num" style={{ fontSize: 'var(--fs-2xl)' }}>
          {days.length}
        </span>
      </section>

      {!readOnly && (
      <form className="card card--pad stack" onSubmit={add}>
        <h3>{t('Dodaj dan')}</h3>
        <div className="grid-2">
          <Field label={t('Datum')} required id="att-date">
            <input
              id="att-date"
              type="date"
              className="input"
              max={today()}
              value={form.attendance_date}
              onChange={(e) => setForm((f) => ({ ...f, attendance_date: e.target.value }))}
            />
          </Field>
          <Field label={t('Opomba')} hint={t('Neobvezno')} id="att-note">
            <input
              id="att-note"
              className="input"
              placeholder={t('npr. teorija, vaja na tleh')}
              value={form.note}
              onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
            />
          </Field>
        </div>
        <button className="btn btn--primary" type="submit" disabled={!form.attendance_date || busy}>
          {busy ? t('Dodajam …') : t('+ Dodaj dan')}
        </button>
      </form>
      )}

      {days.length === 0 ? (
        <div className="card">
          <Empty
            icon="calendar"
            title={t('Še ni zabeleženih dni')}
            hint={t('Ko odkljukaš vajo ali vpišeš let, se dan pojavi tukaj sam.')}
          />
        </div>
      ) : (
        <div className="card list">
          {days.map((day) => (
            <div key={day.date} className="stack stack--tight list__row" style={{ alignItems: 'stretch' }}>
              <div className="row row--wrap">
                <strong className="t-md t-brand">{fmtDate(day.date)}</strong>

                {day.exercises.length > 0 && (
                  <span className="badge badge--brand num">
                    {day.exercises.length} {vaje(day.exercises.length)}
                  </span>
                )}
                {day.flights > 0 && (
                  <span className="badge badge--ok num">
                    {day.flights} {letiWord(day.flights)}
                  </span>
                )}
                {day.manual && <span className="badge badge--mute">{t('Ročno')}</span>}

                <span className="spacer" />

                {day.manual && !readOnly && (
                  <button
                    className="btn-icon btn-icon--sm"
                    onClick={() => remove(day)}
                    aria-label={t('Izbriši dan {d}', { d: fmtDate(day.date) })}
                  >
                    <Icon name="x" size={15} />
                  </button>
                )}
              </div>

              {day.note && (
                <p className="row t-sm t-dim" style={{ gap: 'var(--s-2)' }}>
                  <Icon name="note" size={14} /> {day.note}
                </p>
              )}

              {day.exercises.length > 0 && (
                <ul className="t-sm t-dim" style={{ listStyle: 'none' }}>
                  {day.exercises.map((e, i) => (
                    <li key={i}>
                      <Icon
                        name={e.status === 'confirmed' ? 'check' : 'clock'}
                        size={13}
                        className={e.status === 'confirmed' ? 't-ok' : 't-wait'}
                        style={{ display: 'inline-block', verticalAlign: '-2px' }}
                      />{' '}
                      {e.subTitle}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      )}

      <p className="t-xs t-dimmer">
        {t('Skupaj')} {days.length} {dni(days.length)} {t('šolanja')}.
      </p>
    </div>
  );
}
